import React from "react";
import { Truck, ShieldCheck, HardHat, Wrench, MessageCircle } from "lucide-react";

// Sección 9.9 del PRD v1.5 — Políticas de entrega y servicio.
// Texto fijo aprobado por el cliente. Solo los periodos de garantía y los
// datos formales de la empresa vienen de Configuración de empresa.
//
// IMPORTANTE:
// - NO incluye porcentaje de anticipo (el esquema 70/30 sigue viviendo
//   en la nota predeterminada de "cotización de servicio").
// - Los tres periodos de garantía llegan por prop `warranties`.

const FALLBACK_WARRANTIES = {
  electrical: "1 año",
  equipment: "Garantía de fábrica del proveedor",
  labor: "3 meses",
};

export function DeliveryServicePoliciesContent({ warranties, formal, variant = "landing" }) {
  const w = { ...FALLBACK_WARRANTIES, ...(warranties || {}) };
  const isDark = variant === "landing-dark";
  const textMuted = isDark ? "text-white/70" : "text-slate-600";
  const textBody = isDark ? "text-white/90" : "text-slate-700";
  const heading = isDark ? "text-white" : "text-[#0A1118]";
  const accent = isDark ? "text-[#4ECCC8]" : "text-[#005A91]";
  const cardBg = isDark ? "bg-white/5 border-white/10" : "bg-white border-[#005A91]/10";

  return (
    <div className="space-y-8" data-testid="delivery-service-policies">
      {/* 1. Políticas de entrega */}
      <div className={`${cardBg} border rounded-lg p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`h-10 w-10 rounded-md ${isDark ? "bg-[#4ECCC8]/15" : "bg-[#005A91]/10"} flex items-center justify-center`}>
            <Truck className={`h-5 w-5 ${accent}`} strokeWidth={2.2} />
          </div>
          <h3 className={`text-lg font-black ${heading}`}>1. Políticas de entrega</h3>
        </div>
        <div className={`space-y-4 text-sm ${textBody} leading-relaxed`}>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>1.1 · Plazos de entrega</div>
            <p>
              El tiempo de entrega se define en la cotización/contrato según el alcance del proyecto.
              Ajustes por clima, permisos ante CFE/municipales o cambios solicitados por el cliente se
              notifican por escrito y ajustan el calendario.
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>1.2 · Entrega por etapas</div>
            <p>
              En proyectos de instalación/montaje, la entrega se valida por avances:
              Ingeniería y diseño → Suministro de materiales y equipos → Instalación/montaje →
              Pruebas y puesta en marcha → Entrega final. Cada etapa se valida con firma de
              conformidad del cliente (mismo mecanismo que Reportes de servicio).
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>1.3 · Entrega final</div>
            <p>
              Se agenda visita de entrega final con recorrido junto al cliente; se entrega bitácora
              del trabajo (la que ya genera el módulo de Reportes de servicio), garantías y manual de
              mantenimiento/operación. Detalles menores pendientes se atienden en un plazo de
              <b> 15 días hábiles</b>.
            </p>
          </div>
        </div>
      </div>

      {/* 2. Políticas de servicio */}
      <div className={`${cardBg} border rounded-lg p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`h-10 w-10 rounded-md ${isDark ? "bg-[#4ECCC8]/15" : "bg-[#005A91]/10"} flex items-center justify-center`}>
            <ShieldCheck className={`h-5 w-5 ${accent}`} strokeWidth={2.2} />
          </div>
          <h3 className={`text-lg font-black ${heading}`}>2. Políticas de servicio</h3>
        </div>
        <div className={`space-y-4 text-sm ${textBody} leading-relaxed`}>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>2.1 · Cotización y contrato</div>
            <p>
              Toda cotización tiene vigencia de <b>15 días naturales por defecto</b> (campo configurable
              en cada documento). Para iniciar un proyecto se firma contrato/orden de compra y se
              cubre el anticipo pactado.
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>2.2 · Cambios en el alcance</div>
            <p>
              Cualquier trabajo adicional al alcance cotizado/aprobado genera un <b>&laquo;Concepto Extra&raquo;</b>:
              se cotiza, se aprueba por escrito y luego se ejecuta — no se hacen cambios verbales.
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>2.3 · Visitas a sitio</div>
            <p>
              El cliente puede visitar el sitio/planta en horarios acordados y usando el equipo de
              protección personal (EPP) requerido. Por seguridad, <b>no se permite el ingreso de
              menores de edad</b>.
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>2.4 · Garantías</div>
            <div className="grid sm:grid-cols-3 gap-3 mt-2">
              <WarrantyPill label="Instalaciones eléctricas" value={w.electrical} isDark={isDark} />
              <WarrantyPill label="Equipos suministrados" value={w.equipment} isDark={isDark} />
              <WarrantyPill label="Mano de obra" value={w.labor} isDark={isDark} />
            </div>
            <p className={`text-xs ${textMuted} mt-3`}>
              No se cubren daños por mal uso, fenómenos naturales o modificaciones hechas por
              terceros ajenos a la empresa.
            </p>
          </div>
        </div>
      </div>

      {/* 3. Condiciones generales */}
      <div className={`${cardBg} border rounded-lg p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`h-10 w-10 rounded-md ${isDark ? "bg-[#4ECCC8]/15" : "bg-[#005A91]/10"} flex items-center justify-center`}>
            <Wrench className={`h-5 w-5 ${accent}`} strokeWidth={2.2} />
          </div>
          <h3 className={`text-lg font-black ${heading}`}>3. Condiciones generales</h3>
        </div>
        <div className={`space-y-4 text-sm ${textBody} leading-relaxed`}>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>3.1 · Materiales y equipos</div>
            <p>
              Se usan materiales/equipos de la calidad aprobada en la cotización; si el cliente
              solicita otra marca o especificación, se ajusta el costo.
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>3.2 · Permisos</div>
            <p>
              Los permisos y trámites ante CFE, municipales o de otras autoridades corren por cuenta
              del cliente, salvo que se contraten como servicio adicional gestionado por la empresa.
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>3.3 · Orden y limpieza</div>
            <p>
              Se entrega el sitio de trabajo con limpieza general; retiro de material sobrante o
              escombro mayor se cotiza aparte si aplica.
            </p>
          </div>
          <div>
            <div className={`text-[11px] font-black tracking-[0.14em] uppercase ${accent} mb-1`}>3.4 · Comunicación</div>
            <p className="flex items-start gap-2">
              <MessageCircle className={`h-4 w-4 mt-0.5 shrink-0 ${accent}`} />
              <span>
                Canal oficial <b>WhatsApp</b> + <b>correo</b>; reporte fotográfico de avance (mismo
                mecanismo que Reportes de servicio); tiempo de respuesta objetivo:
                <b> 24 horas hábiles</b>.
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Datos formales — dinámicos desde Configuración de empresa */}
      {formal && (formal.name || formal.rfc || formal.address || formal.phone) && (
        <div className={`${isDark ? "bg-[#4ECCC8]/8 border-[#4ECCC8]/25" : "bg-slate-50 border-[#005A91]/15"} border rounded-lg p-6`}>
          <div className={`text-[11px] font-black tracking-[0.24em] uppercase ${accent} mb-3 flex items-center gap-2`}>
            <HardHat className="h-3.5 w-3.5" /> Datos formales
          </div>
          <div className={`grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm ${textBody}`}>
            {formal.name && <div><span className={`text-xs uppercase tracking-wider ${textMuted}`}>Razón social</span><div className="font-bold">{formal.name}</div></div>}
            {formal.rfc && <div><span className={`text-xs uppercase tracking-wider ${textMuted}`}>RFC</span><div className="font-bold">{formal.rfc}</div></div>}
            {formal.phone && <div><span className={`text-xs uppercase tracking-wider ${textMuted}`}>Teléfono</span><div className="font-bold">{formal.phone}</div></div>}
            {formal.email && <div><span className={`text-xs uppercase tracking-wider ${textMuted}`}>Correo</span><div className="font-bold break-all">{formal.email}</div></div>}
            {formal.address && <div className="sm:col-span-2"><span className={`text-xs uppercase tracking-wider ${textMuted}`}>Domicilio</span><div className="font-bold">{formal.address}</div></div>}
          </div>
        </div>
      )}
    </div>
  );
}

function WarrantyPill({ label, value, isDark }) {
  return (
    <div className={`rounded-md px-4 py-3 ${isDark ? "bg-white/10 border border-white/15" : "bg-[#005A91]/5 border border-[#005A91]/15"}`}>
      <div className={`text-[10px] font-black tracking-[0.16em] uppercase ${isDark ? "text-[#4ECCC8]" : "text-[#005A91]"}`}>{label}</div>
      <div className={`mt-1 font-bold text-sm ${isDark ? "text-white" : "text-[#0A1118]"}`}>{value}</div>
    </div>
  );
}

export default DeliveryServicePoliciesContent;
